/**
 * Native survey builder + outcome reporting API client (VER-109).
 *
 * Talks to the admin-api routes: /survey-templates (CRUD + publish),
 * /survey-responses (capture), /survey-report/programme/:id (group reports + CSV).
 *
 * `definition` is the builder's SurveyDefinition tree — carried opaquely here so this
 * client does not couple to the builder's evolving internal shape.
 */
import { API_URL, errorFromResponse, getData, postData, patchData, deleteData } from "./rest";

export type SurveyStatus = "Draft" | "Published" | "Archived";

export type PersistedSurveyTemplate = {
    id: number;
    code: string;
    title: string;
    instructions: string;
    scaleCode: string | null;
    higherIsBetter: boolean;
    status: SurveyStatus;
    currentVersion: number;
    definition: unknown;
    isSystemSeed: boolean;
};

export type SaveSurveyPayload = {
    code: string;
    title: string;
    instructions: string;
    scaleCode?: string | null;
    higherIsBetter?: boolean;
    definition: unknown;
    status?: SurveyStatus;
};

const jsonOrThrow = async <T>(res: Response, action: string): Promise<T> => {
    // The reason matters here: the capture routes reject for causes a practitioner can act on —
    // an unenrolled participant, a version that does not belong to the template, a withdrawn
    // consent. Throwing only the status left them unable to tell "retry" from "fix the selection".
    if (!res.ok) throw await errorFromResponse(res, action);
    return (await res.json()) as T;
};

// ── Templates ────────────────────────────────────────────────────────────────

export const listSurveyTemplates = async (): Promise<PersistedSurveyTemplate[]> => {
    const { items } = await getData<{ items: PersistedSurveyTemplate[]; total: number }>("/survey-templates?take=200");
    return items;
};

export const getSurveyTemplate = async (id: number): Promise<PersistedSurveyTemplate> =>
    await getData<PersistedSurveyTemplate>(`/survey-templates/${id}`);

export const createSurveyTemplate = async (payload: SaveSurveyPayload): Promise<PersistedSurveyTemplate> =>
    jsonOrThrow(await postData("/survey-templates", payload), "create survey");

export const updateSurveyTemplate = async (
    id: number,
    payload: Partial<SaveSurveyPayload>,
): Promise<PersistedSurveyTemplate> =>
    jsonOrThrow(await patchData(`/survey-templates/${id}`, payload), "update survey");

export const publishSurveyTemplate = async (id: number): Promise<PersistedSurveyTemplate> =>
    jsonOrThrow(await postData(`/survey-templates/${id}/publish`, {}), "publish survey");

export const deleteSurveyTemplate = async (id: number): Promise<void> => {
    const res = await deleteData(`/survey-templates/${id}`);
    if (!res.ok) throw new Error(`Failed to delete survey (${res.status})`);
};

// ── Response capture ─────────────────────────────────────────────────────────

export type SurveyResponseItemPayload = { questionKey: string; value: number; adjustedValue: number };

export type SurveyAdministration = "Baseline" | "Interim" | "Endpoint";

/**
 * A 1-to-1 capture. `programmeId` and `sessionId` are REQUIRED and there is deliberately no
 * `administration` field: the server infers Baseline / Interim / Endpoint from the session number,
 * so the two can never disagree. Sending one would have no effect — the self-report route ignores
 * it (see routes/api/surveyResponse.ts).
 */
export type CaptureResponsePayload = {
    templateId: number;
    templateVersionId?: number | null;
    participantId?: number | null;
    programmeId: number;
    sessionId: number;
    rawScore?: number | null;
    finalScore?: number | null;
    items: SurveyResponseItemPayload[];
};

export const captureSurveyResponse = async (payload: CaptureResponsePayload): Promise<{ id: number }> =>
    jsonOrThrow(await postData("/survey-responses", payload), "save survey response");

/**
 * One captured response as the SERVER stored it.
 *
 * The report screen reads this back instead of re-banding the score the browser computed. In the
 * happy path they are the same number, which is exactly why the local one is unsafe: when they
 * differ — a save that half-failed, a stale flow, a corrected row — the screen saying "saved" is
 * the one showing the value that was not.
 */
export type PersistedSurveyResponse = {
    id: number;
    templateId: number;
    templateTitle: string | null;
    scaleCode: string | null;
    higherIsBetter: boolean;
    participantId: number | null;
    programmeId: number | null;
    sessionId: number | null;
    sessionNumber: number | null;
    administration: SurveyAdministration;
    entryMode: "SelfReported" | "ProxyEntered";
    rawScore: number | null;
    finalScore: number | null;
    completedAt: string;
};

export const getSurveyResponse = async (id: number): Promise<PersistedSurveyResponse> =>
    await getData<PersistedSurveyResponse>(`/survey-responses/${id}`);

/**
 * Group Model B proxy capture (VER-115).
 *
 * A separate endpoint, not a flag on the payload: the server decides provenance from the route so
 * a client cannot label its own submission as someone else's proxy read. Programme and session are
 * required — the server rejects a proxy read that names neither.
 */
export type CaptureProxyResponsePayload = CaptureResponsePayload & {
    /**
     * Kept on this path only. A facilitator recording a whole-group read is stating which
     * measurement point it represents — a judgement the session number does not make for them.
     * The 1-to-1 path has no equivalent field; there the session decides.
     */
    administration: SurveyAdministration;
};

export const captureProxySurveyResponse = async (payload: CaptureProxyResponsePayload): Promise<{ id: number }> =>
    jsonOrThrow(await postData("/survey-responses/proxy", payload), "save proxy read");

/** One group proxy read as listed on the proxy history page. Never carries a participant. */
export type GroupProxyRead = {
    id: number;
    programmeId: number | null;
    programmeName: string | null;
    sessionId: number | null;
    sessionNumber: number | null;
    templateId: number;
    templateTitle: string | null;
    administration: string;
    rawScore: number | null;
    finalScore: number | null;
    recordedAt: string;
    /** The facilitator who recorded it — staff attribution, not participant data. */
    recordedBy: string | null;
};

/**
 * One page of group proxy reads. The endpoint caps the list, so `total` and `truncated` travel
 * with it: dropping them made a capped page indistinguishable from a complete history, and a
 * reader who cannot tell the difference will read "20 group reads" as "there were only 20".
 */
export type GroupProxyReadPage = {
    items: GroupProxyRead[];
    total: number;
    truncated: boolean;
};

export const listGroupProxyReads = async (programmeId?: number): Promise<GroupProxyReadPage> =>
    getData<GroupProxyReadPage>(`/survey-responses/proxy${programmeId ? `?programmeId=${programmeId}` : ""}`);

// ── Reporting ────────────────────────────────────────────────────────────────

export type Descriptives = {
    n: number;
    mean: number | null;
    min: number | null;
    max: number | null;
    range: number | null;
    sd: number | null;
};

export type ProgrammeOutcomeReport = {
    scaleCode: string;
    scaleName: string;
    higherIsBetter: boolean;
    baseline: Descriptives;
    endpoint: Descriptives;
    tTest: {
        computable: boolean;
        n: number;
        meanDiff: number | null;
        t: number | null;
        df: number | null;
        p: number | null;
        note?: string;
    };
    effectSize: { computable: boolean; d: number | null; magnitude: string | null; note?: string };
    summary: string;
    participantsWithData: number;
    participantsPaired: number;
    /**
     * Set by the server when the paired group is too small to report without exposing an
     * individual's score. When true, every figure above is already null — the client renders
     * the notice, it does not do the hiding.
     */
    suppressed: boolean;
    /** The floor the server applied, so the notice can state it without hardcoding a number. */
    minimumReportableGroup: number;
};

/**
 * Group observations recorded for one session (VER-115, shaped by Sean's PR #5 review).
 *
 * A group proxy read describes a whole group and has no participant attached, so it can neither be
 * paired into the pre/post statistics above nor be exploded into individual rows. It is reported
 * here instead: counted always, valued only when the group was big enough that a mean is not one
 * person's score plus arithmetic.
 */
export type GroupObservationSession = {
    sessionId: number | null;
    sessionNumber: number | null;
    /** People in the session — what the small-group floor is applied to. */
    groupSize: number;
    /** How many readings were recorded. Disclosed even when the figures below are withheld. */
    observations: number;
    mean: number | null;
    min: number | null;
    max: number | null;
    recordedAt: number;
    /** True when the group was below the floor and the figures were withheld server-side. */
    suppressed: boolean;
};

export type GroupObservationScale = {
    scaleCode: string;
    scaleName: string;
    higherIsBetter: boolean;
    observations: number;
    sessions: GroupObservationSession[];
    minimumReportableGroup: number;
};

export type ProgrammeReportResponse = {
    programmeId: number;
    reports: ProgrammeOutcomeReport[];
    /** Empty when the programme has no group proxy reads — the 1-to-1 case. */
    groupObservations: GroupObservationScale[];
};

export const getProgrammeOutcomeReports = async (programmeId: number): Promise<ProgrammeReportResponse> =>
    await getData(`/survey-report/programme/${programmeId}`);

/**
 * Trigger an authenticated download of the raw item-level CSV export.
 *
 * The CSV route sits behind `auth` (x-auth-token header), so a plain anchor href would
 * 401. We fetch it ourselves with the token and — crucially — check `response.ok` and
 * THROW on failure, so callers can surface a real error. (The shared getFile() helper
 * does not check status and would silently "download" an error body as a file.)
 */
export const downloadProgrammeSurveyCsv = async (programmeId: number): Promise<void> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_URL}/survey-report/programme/${programmeId}/csv`, {
        method: "GET",
        headers: token ? { "x-auth-token": token } : {},
    });
    if (!response.ok) {
        throw new Error(`Failed to export survey CSV (${response.status})`);
    }

    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") ?? "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] ?? `programme-${programmeId}-survey-raw.csv`;

    const objectUrl = window.URL.createObjectURL(blob);
    try {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        link.click();
    } finally {
        window.URL.revokeObjectURL(objectUrl);
    }
};

/**
 * Absolute URL of the raw CSV export. NOTE: the endpoint requires the x-auth-token
 * header, so this URL is NOT usable as a bare anchor href (it will 401). Prefer
 * downloadProgrammeSurveyCsv(). Kept for callers that construct authenticated requests.
 */
export const programmeSurveyCsvUrl = (programmeId: number): string =>
    `${API_URL}/survey-report/programme/${programmeId}/csv`;

// ── Participant impact (VER-59) ────────────────────────────────────────────────

/** One administration of a scale for a single participant (either the pre or the post). */
export type ParticipantScaleAdministration = {
    score: number;
    administeredAt: string; // ISO 8601
};

/**
 * A single scale's pre/post pair for ONE participant WITHIN ONE PROGRAMME. A
 * participant enrolled in several programmes gets one entry per (programme, scale) —
 * pre/post are never merged across programmes, which would misrepresent the change.
 * `delta` is the raw post − pre (null until both administrations exist); the UI decides
 * improved/declined via `higherIsBetter`, so scale polarity is respected on the client,
 * not baked into delta.
 */
export type ParticipantScaleImpact = {
    programmeId: number;
    programmeName: string;
    scaleCode: string;
    scaleName: string;
    higherIsBetter: boolean;
    pre: ParticipantScaleAdministration | null;
    post: ParticipantScaleAdministration | null;
    delta: number | null;
};

/** One in-session mood reading (VER-118). Sessions 2–5 only; 1–5 holistic rating. */
export type ParticipantMoodPoint = {
    sessionNumber: number;
    score: number;
    administeredAt?: string;
    capturedAt: string;
};

/**
 * A participant's mood readings for one programme, ordered by SESSION (not capture time).
 * Deliberately a sibling of `scales`, never a member of it: a holistic check-in is not an
 * administration of a validated instrument and must not be rendered as one.
 */
export type ParticipantMoodSeries = {
    programmeId: number;
    /** Named so a programme with mood but no survey scores still gets a heading. Null pre-VER-118 rows. */
    programmeName: string | null;
    points: ParticipantMoodPoint[];
};

/** Read-only per-scale impact view for one participant (native SurveyResponse data). */
export type ParticipantImpactResponse = {
    participantId: number;
    scales: ParticipantScaleImpact[];
    /** VER-118 — in-session mood, one series per programme. Empty when nothing was captured. */
    mood: ParticipantMoodSeries[];
};

/**
 * Fetch a single participant's pre/post scale impacts plus their in-session mood series.
 * Org-scoped server-side via `req.prisma` — a participant outside the caller's org 404s
 * (rejected by getData). Read-only surface.
 */
export const getParticipantImpact = async (participantId: number): Promise<ParticipantImpactResponse> =>
    await getData(`/survey-report/participant/${participantId}`);
